export type SoundPreferences = {
  cues: boolean;

  speech: boolean;

  volume: number;
};

const KEY = "bsmeet.sound";

export const DEFAULT_SOUND_PREFERENCES: SoundPreferences = {
  cues: true,
  speech: true,
  volume: 0.7,
};

function clampVolume(value: unknown): number {
  if (typeof value !== "number" || Number.isNaN(value)) return DEFAULT_SOUND_PREFERENCES.volume;
  return Math.min(1, Math.max(0, value));
}

function load(): SoundPreferences {
  try {
    const raw = globalThis.localStorage.getItem(KEY);
    if (raw === null) return DEFAULT_SOUND_PREFERENCES;
    const stored = JSON.parse(raw) as Partial<SoundPreferences>;
    return {
      cues: stored.cues ?? DEFAULT_SOUND_PREFERENCES.cues,
      speech: stored.speech ?? DEFAULT_SOUND_PREFERENCES.speech,
      volume: clampVolume(stored.volume),
    };
  } catch {
    return DEFAULT_SOUND_PREFERENCES;
  }
}

let current: SoundPreferences | null = null;
const listeners = new Set<() => void>();

export function getSoundPreferences(): SoundPreferences {
  current ??= load();
  return current;
}

export function setSoundPreference<K extends keyof SoundPreferences>(
  key: K,
  value: SoundPreferences[K],
): void {
  const next = { ...getSoundPreferences(), [key]: value };
  next.volume = clampVolume(next.volume);
  current = next;
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(next));
  } catch (error) {
    console.error("sound preference save failed:", error);
  }
  for (const listener of listeners) listener();
}

export function subscribeSoundPreferences(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
